<template>
  <transition name="drop-bottom">
    <div
      v-show="show"
      ref="popover"
      class="lp-datepicker-popout"
      :class="[zoomOut && 'zoom-out']"
      :style="{ '--popout-column-definition': `repeat(${columnCount}, 1fr)` }"
      @mousedown.prevent
    >
      <div class="lp-datepicker-heading">
        <button
          class="lp-datepicker-heading-button"
          :disabled="leftDisabled"
          @click.stop.prevent="$emit('left')"
        >
          <lp-icon name="caret-left" />
        </button>
        <component
          :is="'button'"
          class="lp-datepicker-heading-center"
          @click.stop.prevent="$emit('heading')"
        >
          <slot name="heading" />
          <span v-if="headingClickable"><lp-icon name="caret-down" /></span>
        </component>
        <button
          class="lp-datepicker-heading-button"
          :disabled="rightDisabled"
          @click.stop.prevent="$emit('right')"
        >
          <lp-icon name="caret-right" />
        </button>
      </div>
      <div class="lp-datepicker-body">
        <template v-if="'subheading' in $slots">
          <div class="lp-datepicker-subheading">
            <slot name="subheading" />
          </div>
          <hr class="lp-datepicker-divider" />
        </template>
        <div class="lp-datepicker-elements">
          <slot name="body">
            <button
              v-for="item in items"
              :key="item.key"
              :disabled="item.disabled"
              :class="{ selected: item.selected }"
              data-ua-multiple-id="ua-datepicker-option"
              :data-ua-value="item.display"
              @click.stop.prevent="$emit('elementClick', item.value)"
            >
              {{ item.display }}
              <div
                v-if="hasHighlight(item.value)"
                class="notification-dot-container datepicker-notifications"
              >
                <div
                  v-for="highlight in highlights"
                  :key="highlight.date"
                  :style="`${
                    isSameDay(highlight.date, item.value)
                      ? ''
                      : 'display: none;'
                  }`"
                  class="notification-dot-sm"
                  :class="`bg-${highlight.color}`"
                />
              </div>
            </button>
          </slot>
        </div>
      </div>
      <div v-if="highlights" class="lp-highlight-datepicker-tags p-1">
        <span
          v-for="highlight in highlights"
          :key="highlight.date"
          class="tag"
          :class="`tag-${highlight.color}`"
        >
          {{ highlight.label }}
        </span>
      </div>
      <lp-icon name="" />
    </div>
  </transition>
</template>

<script lang="ts">
import { isValid, isSameDay } from 'date-fns'
import { defineComponent, PropType, ref } from 'vue'
import { usePopover } from '@/hooks'
import { Positions } from '@/models/enums'

type Item = {
  key: string
  value: Date
  display: number | string
  disabled: boolean
  selected: boolean
}
type Highlight = {
  label: string
  date: Date
  color: string
}

export default defineComponent({
  props: {
    headingClickable: {
      type: Boolean,
      default: false,
    },
    leftDisabled: {
      type: Boolean,
      default: false,
    },
    rightDisabled: {
      type: Boolean,
      default: false,
    },
    columnCount: {
      type: Number,
      default: 7,
    },
    items: {
      type: Array as PropType<Item[]>,
      default: (): Item[] => [],
    },
    highlights: {
      type: Array as PropType<Highlight[]>,
      default: (): Highlight[] => [],
    },
    placement: {
      type: String,
      default: Positions.T,
      validator: (value: Positions) => Object.values(Positions).includes(value),
    },
    permanent: Boolean,
    autoposition: { type: Boolean, default: true },
    zoomOut: { type: Boolean, default: false },
  },
  emits: {
    elementClick: (value: Date) => isValid(value),
    left: () => true,
    right: () => true,
    heading: () => true,
  },
  setup(props) {
    const popover = ref(null)
    const trigger = ref(null)

    const {
      show,
      placement,
      disabled,
      clickable,
      transition,
      visionTimer,
      position,
      handleMouseEnter,
      handleMouseLeave,
      hidePopover,
      showPopover,
      setPopoverPosition,
    } = usePopover({
      ...props,
      popover,
      trigger,
      arrowCalculation: false,
    })

    const hasHighlight = (pickerDate: Date) => {
      return props.highlights.find((highlight) =>
        isSameDay(highlight.date, pickerDate),
      )
    }

    return {
      show,
      placement,
      disabled,
      clickable,
      transition,
      visionTimer,
      popover,
      trigger,
      position,
      handleMouseEnter,
      handleMouseLeave,
      hidePopover,
      showPopover,
      setPopoverPosition,
      isSameDay,
      hasHighlight,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_variables.scss';

.lp-datepicker {
  position: relative;

  &-preview {
    display: inline;
  }
}

.lp-datepicker-popout {
  border-radius: $border-radius-lg;
  z-index: $z-index-popover;
  position: absolute;
  text-align: center;
  border: 1px solid var(--black-20);
  background: var(--white);
  margin-top: 0.5rem;
  max-width: 26rem;
  box-shadow: $box-shadow-lg;

  &.zoom-out {
    transform: scale(0.85);
    transform-origin: 0 0;
  }

  button + button,
  .btn + .btn {
    margin-left: unset;
  }
}

.lp-datepicker-popout * {
  font-weight: inherit;
}

.lp-datepicker-popout button {
  background: none;
  border: none;
  outline: none;
}

.lp-datepicker-popout button:not(:disabled) {
  cursor: pointer;
}

.lp-datepicker-heading,
.lp-datepicker-body {
  padding: 0.5rem;
}

.lp-datepicker-heading {
  width: 100%;
  display: flex;
  border-bottom: 1px solid var(--black-20);

  &-center {
    flex: 1;

    span {
      margin-left: 0.5rem;
    }
  }
}

.lp-datepicker-subheading,
.lp-datepicker-body .lp-datepicker-elements {
  display: grid;
  grid-template-columns: var(--popout-column-definition);
  gap: 0.5rem 0.3rem;

  button {
    height: 2.5rem;
    width: 2.5rem;
    position: relative;
  }
}

.lp-datepicker-subheading {
  font-size: 0.75rem;
  text-transform: uppercase;
  margin-top: 1em;
  color: var(--black-30);
  font-weight: var(--font-semibold);
}

.lp-datepicker-divider {
  border: 1px solid var(--divider-color);
  border-radius: $border-radius-base;
}

.lp-datepicker-body .lp-datepicker-elements button:disabled {
  border: 0;
}

.lp-datepicker-body .lp-datepicker-elements button {
  color: unset;
  font-size: var(--font-size-xs);
  border-radius: $border-radius-full;
}

.lp .lp-datepicker-heading button,
.lp .lp-datepicker-heading button:hover {
  color: unset;
  border: 0;
}

.lp-datepicker-body .lp-datepicker-elements button span {
  display: block;
  line-height: 1.9em;
  height: 1.8em;
  border-radius: var(--elem-border-radius);
}

.lp-datepicker-body .lp-datepicker-elements button:not(:disabled):hover {
  border: 1px solid var(--primary);
}

.lp-datepicker-body .lp-datepicker-elements button.selected,
.lp-datepicker-body .lp-datepicker-elements button.selected span {
  background-color: var(--primary);
  color: var(--white);
}

.lp-datepicker-body .lp-datepicker-elements button {
  margin-left: 0;
  padding: 10px !important;
  border: 1px solid transparent;
}

.lp-datepicker-body .lp-datepicker-elements button:disabled {
  background: transparent !important;
  color: var(--black-30);
}

.lp-datepicker-popout .lp-btn--neutral {
  box-shadow: none;
}

.lp .datepicker-notifications {
  position: absolute;
  top: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 0.2rem;

  .notification-dot-sm {
    height: 0.5rem;
    width: 0.5rem;
    position: relative;
  }
}

.lp-highlight-datepicker-tags {
  text-align: left;

  .tag {
    margin: 0 5px 5px 0;
  }
}
</style>
